import { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'state/hooks';

import { PROCESS_STATUS } from 'appConstants';
import { ProcessStatus, Scenario } from 'appTypes';

import {
  setScenarioRunUUID,
  setScenarioStatus,
  useGetScenarioStatusQuery,
} from '../../state/detourSlices';
import {
  selectCurrentScenarioId,
  selectProject,
} from '../../state/workflowSlice';

const terminalStatuses: ProcessStatus[] = [
  PROCESS_STATUS.DONE,
  PROCESS_STATUS.ERROR,
];

function useQueryScenarioStatus(scenario: Scenario): ProcessStatus {
  const project_slug = useAppSelector(selectProject);
  const [currStatus, setCurrStatus] = useState<ProcessStatus>(scenario.status);

  const skip =
    !project_slug ||
    !scenario.scenario_id || // scenario doesn't have a scenario run - something went wrong
    !scenario.uuid ||
    (currStatus ? terminalStatuses.includes(currStatus) : false);

  const { data } = useGetScenarioStatusQuery(
    { project_slug, scenario_id: scenario.scenario_id },
    {
      skip,
      pollingInterval: 3 * 1000,
    },
  );
  // update from server
  useEffect(() => {
    const status = data?.status as ProcessStatus;
    if (status && status !== currStatus) setCurrStatus(status);
  }, [data?.status]);

  // update from external changes to the scenario (edit case)
  useEffect(() => {
    if (scenario?.status && scenario.status !== currStatus)
      setCurrStatus(scenario.status);
  }, [scenario?.status]);

  return currStatus;
}

export function useScenarioStatus(scenario: Scenario) {
  const dispatch = useAppDispatch();
  const currentScenarioId = useAppSelector(selectCurrentScenarioId);
  const status = useQueryScenarioStatus(scenario);
  const { scenario_id } = scenario;

  useEffect(() => {
    if (currentScenarioId && currentScenarioId === scenario_id) {
      dispatch(setScenarioStatus(status));
      dispatch(setScenarioRunUUID(scenario?.uuid));
    }
  }, [status, currentScenarioId, scenario?.uuid]);

  useEffect(() => {
    if (!currentScenarioId) {
      dispatch(setScenarioStatus(undefined));
      dispatch(setScenarioRunUUID(undefined));
    }
  }, [currentScenarioId]);

  return status;
}
